/*
 * Copyright (c) 2022 Puratos. All rights reserved.
 */

import React, {FunctionComponent} from "react";
import {Field} from "redux-form";
import {Typeahead} from 'react-bootstrap-typeahead';
import {useI18N} from "../../my-puratos/hoc/withI18N";
import {validAutoSuggestMultiValue, validAutoSuggestSingleValue} from "../../../utils/my-puratos/FormUtils";

interface IProps {
    name: string,
    resultKey: string,
    label?: string,
    options: any[],
    paginate?: boolean,
    maxLength?: number,
    multiple?: boolean,
    disabled?: boolean,
    isRequired?: boolean,
    insertedOption: string,
    selectedOption: any[],
    className?: string,
    infoMsg?: string,
    errorMsg?: string,
    hasError?: boolean,
    emptyLabel?: string,
    maxResults?: number,
    onInputChange?(value: string): void,
    setSelectedInput?(value: any): void,
}

export const TypeAheadField: FunctionComponent<IProps> = ({name, resultKey, label, options = [], paginate = false, maxLength, multiple = false, disabled, isRequired = false, insertedOption, selectedOption, className = "", infoMsg = "", errorMsg = "", hasError = false, emptyLabel, onInputChange, setSelectedInput, maxResults}) => {

    const valueValid = multiple ?
        validAutoSuggestMultiValue(insertedOption, selectedOption, isRequired) :
        validAutoSuggestSingleValue(insertedOption, selectedOption);

    return <Field name={name} component={TypeAheadInput} label={label} maxLength={maxLength} disabled={disabled}
                  validate={[valueValid]} required={isRequired} options={options} className={className}
                  infoMsg={infoMsg} paginate={paginate} onInputChange={onInputChange}
                  setSelectedInput={setSelectedInput} errorMsg={errorMsg} hasError={hasError}
                  resultKey={resultKey} emptyLabel={emptyLabel} multiple={multiple} maxResults={maxResults}/>

}

const TypeAheadInput = ({input, meta, resultKey, label, options, paginate, maxLength, multiple, disabled, required, infoMsg, errorMsg, hasError, emptyLabel, onInputChange, setSelectedInput, maxResults, ...props}) => {

    const [selectedValue, setSelectedValue] = React.useState([]);

    const translate = useI18N();
    const error = (hasError && !meta.valid) || (meta.touched && !meta.valid && (input?.value?.length > 0 || selectedValue?.length > 0));
    const inputs = {...input, onChange: (selected) => setSelectedValue(selected)};

    const infoMessage = multiple && maxResults ? `${translate("Please be aware that only max. {} products can be added.").replace("{}", maxResults)}${infoMsg ? "\n".concat(infoMsg) : ''}` : infoMsg;

    React.useEffect(() => {
        onInputChange && onInputChange(selectedValue[0] && selectedValue[0][resultKey]);
        setSelectedInput && setSelectedInput(selectedValue);
    }, [selectedValue]);

    return (
        <div className="row">
            <div className={`col-md-6 col-sm-12 form--input${multiple ? "-multi" : ""} ${error && !multiple ? "form--input__error" : ""} ${error && multiple ? "form--input__error-multi" : ""} ${meta.active || input.value || selectedValue?.length > 0 ? "form--input__content" : ""}`}>
                <label htmlFor={input.name}>{required && <span>*</span>} {translate(label)}</label>
                <div className="p-relative">
                    <Typeahead options={options} id={input.name} selected={selectedValue} minLength={2} maxResults={5}
                               onInputChange={(value) => onInputChange && onInputChange(value)} disabled={disabled}
                               maxLength={maxLength} labelKey={resultKey} paginate={paginate} emptyLabel={emptyLabel}
                               multiple={multiple} inputProps={{required: required, readOnly: multiple && selectedValue.length >= maxResults}} className={"form-icon"}
                               autocomplete="off" {...inputs} {...props}/>
                    {multiple && <span className={"svg-search-black-circle"} />}
                </div>
                <span className={"text-very-small text-pre-line"}>{translate(!error ? infoMessage : errorMsg)?.replace("{}", translate(label))}</span>
            </div>
        </div>
    );

}
